/**
 * React Starter Kit (https://www.reactstarterkit.com/)
 *
 * Copyright © 2014-present Kriasoft, LLC. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.txt file in the root directory of this source tree.
 */
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'whatwg-fetch';
import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import deepForceUpdate from 'react-deep-force-update';
import queryString from 'query-string';
import { createPath } from 'history';
import { inspect } from '@xstate/inspect';
import Cookies from 'universal-cookie';

import { clearFetchError, leavePage } from './actions/FetchActions';
import App from './components/App';
import createFetch from './createFetch';
import history from './history';
import { updateMeta } from './DOMUtils';
import router from './router';
import configureStore from './store';
import { XSTATE_DEBUG_COOKIES, IS_LOCAL_XSTATE_VIZ } from './settings';

// Initialize Sentry for production, testing, and staging (but not development)
if (process?.env.SENTRY_DSN && process?.env.NODE_ENV !== 'development') {
  Sentry.init({ dsn: process.env.SENTRY_DSN });
}

const manageXStateInspection = () => {
  // The inspector must be loaded before any of the state machines
  //  or the state will not register in the inspector
  const cookies = new Cookies();
  const activeDebugModes = XSTATE_DEBUG_COOKIES.filter(
    cookie => cookies.get(cookie) === 'true',
  );
  const isLocalXStateViz = cookies.get(IS_LOCAL_XSTATE_VIZ) === 'true';

  if (process?.env.NODE_ENV !== 'production' && activeDebugModes.length > 0) {
    console.info('XState inspection: ON');
    inspect({
      url: isLocalXStateViz
        ? 'http://localhost:3421/viz?inspect' // change this to your local viz server's URL
        : 'https://stately.ai/viz?inspect',
      iframe: false,
    });
  }
  return activeDebugModes;
};

const activeDebugModes = manageXStateInspection();

const isUsingDevTools = cookieName => {
  return (
    process?.env.NODE_ENV !== 'production' &&
    activeDebugModes &&
    activeDebugModes.includes(cookieName)
  );
};

// Enables critical path CSS rendering
// https://github.com/kriasoft/isomorphic-style-loader
const insertCss = (...styles) => {
  // eslint-disable-next-line no-underscore-dangle
  const removeCss = styles.map(x => x._insertCss());
  return () => {
    removeCss.forEach(f => f());
  };
};

// Global (context) variables that can be easily accessed from any React component
// https://facebook.github.io/react/docs/context.html
const context = {
  // Universal HTTP client
  fetch: createFetch(fetch, {
    baseUrl: window.App.apiUrl,
  }),
  // Initialize a new Redux store
  // http://redux.js.org/docs/basics/UsageWithReact.html
  store: configureStore(window.App.state, { fetch, history }),
  storeSubscription: null,
  isUsingDevTools,
};

const container = document.getElementById('app');
let currentLocation = history.location;
let appInstance;

const scrollPositionsHistory = {};

// Re-render the app when window.location changes
async function onLocationChange(location, action) {
  const previousPathname = currentLocation.pathname;
  const previousUrl = `${previousPathname}${currentLocation.search}`;
  // Remember the latest scroll position for the previous location
  scrollPositionsHistory[currentLocation.key] = {
    scrollX: window.pageXOffset,
    scrollY: window.pageYOffset,
  };
  // Delete stored scroll position for next page if any
  if (action === 'PUSH') {
    delete scrollPositionsHistory[location.key];
  }
  currentLocation = location;

  const isInitialRender = !action;
  try {
    context.pathname = location.pathname;
    context.query = queryString.parse(location.search);

    // Clear the current global banner when the URL represents a departure,
    // and retire the page so a request it started cannot raise a banner on
    // the destination. Pages also rewrite their own URL while loading; those
    // replacements keep the current banner.
    const leftThePage =
      previousPathname !== location.pathname ||
      (previousUrl !== `${location.pathname}${location.search}` &&
        action !== 'REPLACE');
    if (leftThePage) {
      leavePage();
      context.store.dispatch(clearFetchError());
    }

    // Traverses the list of routes in the order they are defined until
    // it finds the first route that matches provided URL path string
    // and whose action method returns anything other than `undefined`.
    const route = await router.resolve(context);

    // Prevent multiple page renders during the routing process
    if (currentLocation.key !== location.key) {
      return;
    }

    if (route.redirect) {
      history.replace(route.redirect);
      return;
    }

    const renderReactApp = ReactDOM.render;

    appInstance = renderReactApp(
      <App context={context} insertCss={insertCss}>
        {route.component}
      </App>,
      container,
      () => {
        if (isInitialRender) {
          // Switch off the native scroll restoration behavior and handle it manually
          // https://developers.google.com/web/updates/2015/09/history-api-scroll-restoration
          if (window.history && 'scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
          }

          const elem = document.getElementById('css');
          if (elem) elem.parentNode.removeChild(elem);
          return;
        }

        document.title = route.title;

        updateMeta('description', route.description);
        // Update necessary tags in <head> at runtime here, ie:
        // updateMeta('keywords', route.keywords);
        // updateCustomMeta('og:url', route.canonicalUrl);
        // updateCustomMeta('og:image', route.imageUrl);
        // updateLink('canonical', route.canonicalUrl);
        // etc.

        let scrollX = 0;
        let scrollY = 0;
        const pos = scrollPositionsHistory[location.key];
        if (pos) {
          // eslint-disable-next-line prefer-destructuring
          scrollX = pos.scrollX;
          // eslint-disable-next-line prefer-destructuring
          scrollY = pos.scrollY;
        } else {
          const targetHash = location.hash.substr(1);
          if (targetHash) {
            const target = document.getElementById(targetHash);
            if (target) {
              scrollY = window.pageYOffset + target.getBoundingClientRect().top;
            }
          }
        }

        // Restore the scroll position if it was saved into the state
        // or scroll to the given #hash anchor
        // or scroll to top of the page
        window.scrollTo(scrollX, scrollY);

        // Google Analytics tracking. Don't send 'pageview' event after
        // the initial rendering, as it was already sent
        if (window.ga) {
          window.ga('send', 'pageview', createPath(location));
        }
      },
    );
  } catch (error) {
    if (__DEV__) {
      throw error;
    }

    console.error(error);

    // Do a full page reload if error occurs during client-side navigation
    if (!isInitialRender && currentLocation.key === location.key) {
      console.error('Will reload your page after error');
      window.location.reload();
    }
  }
}

// Handle client-side navigation by using HTML5 History API
// For more information visit https://github.com/mjackson/history#readme
history.listen(onLocationChange);
onLocationChange(currentLocation);

// Enable Hot Module Replacement (HMR)
if (module.hot) {
  module.hot.accept('./router', () => {
    if (appInstance && appInstance.updater.isMounted(appInstance)) {
      // Force-update the whole tree, including components that refuse to update
      deepForceUpdate(appInstance);
    }

    onLocationChange(currentLocation);
  });
}
